import {DocxTextError, extractDocxRawText} from './fenzsafeDocxText';
import {emlPreviewText, EmlTextError, extractEmlPlainText, MAX_EML_PARSE_BYTES} from './fenzsafeEmlText';

/** Preview limits are independent of upload limits. Originals are never rewritten. */
export const PREVIEW_MAX_BYTES = 25 * 1024 * 1024;
export const PREVIEW_MAX_TEXT_BYTES = 1024 * 1024;
export const PREVIEW_MAX_DOCX_BYTES = 20_000_000;
export const PREVIEW_MAX_EML_BYTES = MAX_EML_PARSE_BYTES;
export const PREVIEW_MAX_PIXELS = 16_000_000;
export const PREVIEW_TIMEOUT_MS = 20_000;

export type PreviewContent =
  | {kind: 'text'; text: string}
  | {kind: 'pdf'; bytes: Uint8Array}
  | {kind: 'image'; bytes: Uint8Array; mime: 'image/png' | 'image/jpeg'; width: number; height: number};

const unsupported = () => new Error('This file cannot be displayed here. You can still use Download to open the original in a suitable application.');

export class DocxPreviewError extends Error {
  constructor() {
    super('docx_unreadable');
    this.name = 'DocxPreviewError';
  }
}

export class EmlPreviewError extends Error {
  constructor() {
    super('eml_unreadable');
    this.name = 'EmlPreviewError';
  }
}

/**
 * Excel (XLS/XLSX) has no preview here, deliberately -- this is a known missing capability, not a
 * per-file failure, so it gets its own error/message rather than falling through to the generic
 * "This file cannot be displayed" case DOCX/EML parse failures also use.
 *
 * Both realistic client-side parser choices for XLSX carry a currently unpatched vulnerability
 * reachable from just reading an untrusted file, re-checked 2026-10-16 (the repo's own prior
 * review, docs/ai/FENZSAFE_DISCOVERY_FEASIBILITY_CONTRACT.md §"XLSX", recommended `exceljs` over
 * `xlsx`/SheetJS and is now OUTDATED on this point -- its exceljs CVE analysis predates two CVEs
 * disclosed since):
 * - `xlsx` (SheetJS Community Edition): CVE-2024-22363, a ReDoS triggerable by a crafted
 *   spreadsheet; the maintainer no longer publishes a fixed free version to npm at all.
 * - `exceljs` (the doc's own recommendation): CVE-2026-78207, prototype pollution via its
 *   `deepMerge` helper, reached from parsing a cell note -- i.e. triggered by reading a file, the
 *   exact thing a viewer does; CISA/NVD-linked sources point the vulnerable code at exceljs's own
 *   upstream 4.4.0 tag (not only the unrelated `exceljs-hardened` fork the prior doc assumed was
 *   the sole target), and no confirmed patched upstream release exists as of this check.
 *   CVE-2026-78206 (unbounded-decompression DoS) is also open against the same version.
 * Neither library is added to this codebase. Server-side rasterization was considered too (the
 * founder's own suggested fallback) and rejected for this slice -- it still needs some real
 * parser to turn XLSX bytes into pixels, so it does not actually avoid this exact risk, only moves
 * where the vulnerable parse happens.
 */
export class XlsxPreviewError extends Error {
  constructor() {
    super('xlsx_unsupported');
    this.name = 'XlsxPreviewError';
  }
}

function image(bytes: Uint8Array, mime: 'image/png' | 'image/jpeg', width: number, height: number): PreviewContent {
  if (!width || !height || width > 16384 || height > 16384 || width * height > PREVIEW_MAX_PIXELS) {
    throw new Error('This image is too large for the viewer. Use Download to open the original.');
  }
  return {kind: 'image', bytes, mime, width, height};
}

/** Never trust the HTTP content-type or a PDF/image filename as a rendering instruction. */
export function identifyPreview(bytes: Uint8Array, name: string): PreviewContent {
  if (!bytes.length || bytes.length > PREVIEW_MAX_BYTES) throw unsupported();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length >= 5 && String.fromCharCode(...bytes.subarray(0, 5)) === '%PDF-') return {kind: 'pdf', bytes};
  if (bytes.length >= 33 && [137, 80, 78, 71, 13, 10, 26, 10].every((v, i) => bytes[i] === v)
    && view.getUint32(8) === 13 && view.getUint32(12) === 0x49484452) {
    return image(bytes, 'image/png', view.getUint32(16), view.getUint32(20));
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    while (offset + 4 <= bytes.length) {
      if (bytes[offset++] !== 0xff) throw unsupported();
      while (bytes[offset] === 0xff) offset++;
      const marker = bytes[offset++];
      if (marker === 0xda || marker === 0xd9 || offset + 2 > bytes.length) break;
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) throw unsupported();
      if ([0xc0, 0xc1, 0xc2].includes(marker) && length >= 8) {
        return image(bytes, 'image/jpeg', view.getUint16(offset + 5), view.getUint16(offset + 3));
      }
      offset += length;
    }
    throw unsupported();
  }
  // Text is rendered only as escaped React text, even if it contains HTML/JavaScript.
  if (/\.(txt|csv|log)$/i.test(name) && bytes.length <= PREVIEW_MAX_TEXT_BYTES) {
    const text = new TextDecoder('utf-8', {fatal: true}).decode(bytes);
    if (/[\x00-\x08\x0b\x0c\x0e-\x1f]/.test(text)) throw unsupported();
    return {kind: 'text', text};
  }
  throw unsupported();
}

/** DOCX parsing is asynchronous; all previously supported formats retain their byte-first path. */
export async function identifyPreviewContent(bytes: Uint8Array, name: string): Promise<PreviewContent> {
  if (/\.eml$/i.test(name)) {
    if (!bytes.length || bytes.length > PREVIEW_MAX_EML_BYTES || bytes.length > PREVIEW_MAX_BYTES) throw new EmlPreviewError();
    try {
      const text = emlPreviewText(await extractEmlPlainText(new Uint8Array(bytes).buffer));
      if (new TextEncoder().encode(text).byteLength > PREVIEW_MAX_TEXT_BYTES || /[\x00-\x08\x0b\x0c\x0e-\x1f]/.test(text)) throw new EmlPreviewError();
      return {kind: 'text', text};
    } catch (error) {
      if (error instanceof EmlPreviewError) throw error;
      if (error instanceof EmlTextError) throw new EmlPreviewError();
      throw new EmlPreviewError();
    }
  }
  if (/\.xlsx?$/i.test(name)) throw new XlsxPreviewError();
  if (!/\.docx$/i.test(name)) return identifyPreview(bytes, name);
  if (!bytes.length || bytes.length > PREVIEW_MAX_DOCX_BYTES || bytes.length > PREVIEW_MAX_BYTES) {
    throw new DocxPreviewError();
  }

  try {
    // Give the parser an exact, private ArrayBuffer copy; never expose or mutate the stored bytes.
    const buffer = new Uint8Array(bytes).buffer;
    const text = await extractDocxRawText(buffer);
    if (new TextEncoder().encode(text).byteLength > PREVIEW_MAX_TEXT_BYTES) throw new DocxPreviewError();
    return {kind: 'text', text};
  } catch (error) {
    if (error instanceof DocxPreviewError) throw error;
    if (error instanceof DocxTextError) throw new DocxPreviewError();
    throw new DocxPreviewError();
  }
}

export function readPreviewBytes(blob: Blob, signal: AbortSignal): Promise<Uint8Array> {
  if (blob.size > PREVIEW_MAX_BYTES) return Promise.reject(unsupported());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const abort = () => { reader.abort(); reject(new Error('Preview cancelled.')); };
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, {once: true});
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(new Error('The file could not be read.'));
    reader.onloadend = () => signal.removeEventListener('abort', abort);
    reader.readAsArrayBuffer(blob);
  });
}

let pdfModule: Promise<typeof import('pdfjs-dist')> | undefined;
export function loadPreviewPdf() {
  pdfModule ??= Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.mjs?url')])
    .then(([pdf, worker]) => { pdf.GlobalWorkerOptions.workerSrc = worker.default; return pdf; })
    .catch((error: unknown) => { pdfModule = undefined; throw error; });
  return pdfModule;
}
